import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { ResourceCreate, ResourceItem, ResourceType, ResourceUpdate } from "../api/ResourcesApi";
import { fetchAvailableRoles } from "features/roles/api/RolesApi";
import type { RoleData } from "features/roles/api/RolesApi";

interface ResourceFormModalProps {
  initial?: ResourceItem | null;
  onSave: (data: ResourceCreate | ResourceUpdate) => Promise<void>;
  onClose: () => void;
}

export default function ResourceFormModal({ initial, onSave, onClose }: ResourceFormModalProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [tag, setTag] = useState(initial?.tag ?? "");
  const [resourceType, setResourceType] = useState<ResourceType>(initial?.resource_type ?? "document");
  const [url, setUrl] = useState(initial?.url ?? "");
  const [roleIds, setRoleIds] = useState<number[]>(initial?.role_ids ?? []);
  const [isEnabled, setIsEnabled] = useState(initial?.is_enabled ?? true);

  const [roles, setRoles] = useState<RoleData[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAvailableRoles().then(setRoles).catch(() => setRoles([]));
  }, []);

  function toggleRole(id: number) {
    setRoleIds((prev) => prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required"); return; }
    if (!url.trim()) { setError("URL is required"); return; }
    if (roleIds.length === 0) { setError("Select at least one role"); return; }

    setSaving(true);
    setError("");
    try {
      const payload: ResourceCreate | ResourceUpdate = {
        title: title.trim(),
        description: description.trim() || undefined,
        tag: tag.trim() || undefined,
        resource_type: resourceType,
        url: url.trim(),
        role_ids: roleIds,
        ...(initial ? { is_enabled: isEnabled } : {}),
      };
      await onSave(payload);
      onClose();
    } catch {
      setError("Failed to save resource");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground font-inter">
            {initial ? "Edit Resource" : "Add Resource"}
          </h2>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {initial && (
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-foreground font-inter">Enabled (visible to employees)</span>
              <button
                type="button"
                onClick={() => setIsEnabled(!isEnabled)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  isEnabled ? "bg-primary" : "bg-border"
                }`}
                role="switch"
                aria-checked={isEnabled}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                    isEnabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Title *</label>
            <input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(""); }}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="e.g. TM Handbook"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Description</label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary resize-none font-inter"
              placeholder="Brief description of this resource…"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Tag</label>
            <input
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="e.g. Strategy, Upwork"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Type *</label>
            <div className="flex gap-2">
              {(["document", "video"] as ResourceType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setResourceType(t)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium font-inter capitalize border transition-colors ${
                    resourceType === t
                      ? "bg-primary/15 border-primary/40 text-primary"
                      : "bg-surface-muted border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">
              {resourceType === "video" ? "Video URL (YouTube, Loom, or other)" : "Document URL"} *
            </label>
            <input
              value={url}
              onChange={(e) => { setUrl(e.target.value); setError(""); }}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder={resourceType === "video" ? "https://www.youtube.com/watch?v=…" : "https://docs.google.com/document/d/…"}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Visible to Roles *</label>
            <div className="flex flex-wrap gap-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => toggleRole(role.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium font-inter border transition-colors ${
                    roleIds.includes(role.id)
                      ? "bg-primary/15 border-primary/40 text-primary"
                      : "bg-surface-muted border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {role.role_name}
                </button>
              ))}
              {roles.length === 0 && (
                <p className="text-xs text-muted-foreground font-inter">No active roles found.</p>
              )}
            </div>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-xl hover:bg-surface-muted font-inter">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium rounded-xl font-inter"
            >
              {saving ? "Saving…" : initial ? "Save Changes" : "Add Resource"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
